import React, { useEffect, useState } from "react";
import { Grid, Typography } from "@mui/material";
import { useLocation } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import { FormattedMessage } from "react-intl";

import style from "./index.module.css";

import GetImages from "@/components/GetImages";
import Overlays from "./Overlays";

import { IRoleType } from "@/models/role";
import { IOrderDateRangeParams } from "@/models/doctor";
import {
  IAppointmentPermissionEnums,
  IModalKeyEnums,
} from "@/models/permission";
import { ISessionKeys } from "@/models";

import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  resetScheduleSelection,
  schedule,
  setIsCancelSchedule,
} from "@/store/slices/doctor/schedule";
import { config, setIsShowUniversalSearch } from "@/store/slices/config";
import {
  fetchDiagnosticRequestOrdersList,
  fetchMorePharmacyRequestOrdersList,
  fetchPharmacyRequestOrdersList,
  saveSelectedOrder,
} from "@/store/slices/pharmacyClinicDiagnostic";
import { ipDashboardDetails } from "@/store/slices/doctor/ipDashboard";

import { storage } from "@/utils/Storage";
import usePagination from "@/utils/hooks/usePagination";
import useMobileTopNavigation from "@/utils/hooks/useMobileTopNavigation";
import { usePermissionRestriction } from "@/utils/hooks/usePermissionRestriction";

interface INavItem {
  path: string;
  icon: React.ReactNode;
  title: string;
  subText: string;
  subTextHandler?: () => void;
  locationIcon: boolean;
  secondIcon: React.ReactNode;
  isShowSearch: boolean;
}
const MobileTopNavigation: React.FC = () => {
  const roleType = storage.getSessionToken(ISessionKeys.ROLE_TYPE);
  const [navItem, setNavItem] = useState<INavItem>();
  const [selectedHospital, setSelectedHospital] = useState<string>("");

  const { pendingOrdersList } = useAppSelector(
    (state) => state.pharmacyClinicDiagnostic
  );
  const { cartDetails } = useAppSelector((state) => state.tests);

  const { handleFetch } = usePagination<IOrderDateRangeParams>({
    fetchFn: fetchPharmacyRequestOrdersList,
    fetchNextFn: fetchMorePharmacyRequestOrdersList,
  });

  const subText = useLocation();
  const { generateNavItems } = useMobileTopNavigation();

  const dispatch = useAppDispatch();
  const { isCancelSchedule, scheduleSelection } = useAppSelector(schedule);

  const { hospitals, selectedHospitalId } = useAppSelector(ipDashboardDetails);
  const { inAppNotification } = useAppSelector(config) || {};

  const permissionRestrictionHandler = usePermissionRestriction();

  const universalSearchHandler = () => {
    const isPermissionGranted = permissionRestrictionHandler(
      IModalKeyEnums.APPOINTMENT,
      IAppointmentPermissionEnums.SEARCH_APPOINTMENT
    );
    if (!isPermissionGranted) return;
    dispatch(setIsShowUniversalSearch(true));
  };

  const setNavItemsData = () => {
    const navItems = generateNavItems(selectedHospital, pendingOrdersList);
    if (!roleType) {
      setNavItem(navItems[IRoleType.PATIENT][0]);
      return;
    }
    if (
      roleType === IRoleType.GUEST ||
      roleType === IRoleType.PATIENT ||
      roleType === IRoleType.DOCTOR ||
      roleType === IRoleType.JR_DOCTOR ||
      roleType === IRoleType.PHARMACY ||
      roleType === IRoleType.DIAGNOSTIC_CENTER
    ) {
      navItems[roleType].forEach((item, index) => {
        if (item.path.includes(subText.pathname)) {
          setNavItem({
            ...item,
            title: selectedHospital ? selectedHospital : item.title,
          });
        }
      });
    }
  };

  const openCloseCancelAppointmentConfirmation = () =>
    dispatch(setIsCancelSchedule(!isCancelSchedule));

  const cancelAppointmentHandler = () => {
    if (
      scheduleSelection?.selectedSchedules?.length === 0 &&
      scheduleSelection?.selectedAppointments?.length === 0
    )
      return;
    const isPermissionGranted = permissionRestrictionHandler(
      IModalKeyEnums.APPOINTMENT,
      IAppointmentPermissionEnums.CANCEL_APPOINTMENT
    );
    if (!isPermissionGranted) return;

    openCloseCancelAppointmentConfirmation();
  };

  useEffect(() => {
    setNavItemsData();
  }, [
    subText,
    selectedHospital,
    pendingOrdersList,
    cartDetails,
    inAppNotification,
  ]);

  useEffect(() => {
    if (subText.pathname.includes("/ip-dash")) {
      const hospital = hospitals?.filter(
        (hospital) => hospital?.hospital_id === selectedHospitalId
      )[0];
      setSelectedHospital(hospital?.name ?? "No Hospital");
    } else {
      setSelectedHospital("");
    }
  }, [selectedHospitalId, subText.pathname]);

  useEffect(() => {
    dispatch(saveSelectedOrder(null));
    if (roleType === IRoleType.DIAGNOSTIC_CENTER) {
      dispatch(fetchDiagnosticRequestOrdersList({}));
    } else if (roleType === IRoleType.PHARMACY) {
      handleFetch({});
    }
  }, []);

  return (
    <>
      <Grid item className={style.topNavigationContainer}>
        <Grid item className={style.topNavigationTitleContainer}>
          {scheduleSelection.isSelectionOn ? (
            <Grid item className={style.selectedScheduleContainer}>
              <CloseIcon
                sx={{ height: 24, weight: 24 }}
                onClick={() => dispatch(resetScheduleSelection())}
              />
              <Grid item className={style.selectedScheduleAppointmentCount}>
                <Typography component={"h4"}>
                  <FormattedMessage
                    id="select_items"
                    defaultMessage="Select items"
                  />
                </Typography>
                <Typography component={"p"}>
                  {scheduleSelection.selectedSchedules.length}{" "}
                  <FormattedMessage id="slots" defaultMessage="Slots" />,{" "}
                  {scheduleSelection.selectedAppointments.length}{" "}
                  <FormattedMessage
                    id="appointments"
                    defaultMessage="appointments"
                  />
                </Typography>
              </Grid>
            </Grid>
          ) : (
            <>
              <Grid item className={style.topNavigationIcon}>
                {navItem?.icon}
              </Grid>
              <Grid item className={style.topNavigationTextContainer}>
                <Typography
                  component={"p"}
                  className={style.topNavigationTitle}
                >
                  {navItem?.title}
                </Typography>
                {navItem?.subText && (
                  <Grid item className={style.topNavigationLocation}>
                    {navItem?.locationIcon && (
                      <GetImages
                        name="DoctorNavLocationIcon"
                        width="12"
                        height="12"
                      />
                    )}
                    <Typography
                      component={"p"}
                      className={style.topNavigationLocationText}
                      onClick={() =>
                        navItem?.subTextHandler && navItem?.subTextHandler()
                      }
                    >
                      {navItem?.subText}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </>
          )}
        </Grid>
        <Grid item className={style.topNavigationIconContainer}>
          {scheduleSelection.isSelectionOn ||
            (navItem?.isShowSearch && (
              <Grid item onClick={universalSearchHandler}>
                <GetImages name="NavSearchIcon" width="24" height="24" />
              </Grid>
            ))}
          {scheduleSelection.isSelectionOn ||
            (navItem?.secondIcon && (
              <Grid item className={style.topNavigationSecondIconContainer}>
                {navItem?.secondIcon}
              </Grid>
            ))}
          {scheduleSelection.isSelectionOn && (
            <Grid
              item
              className={style.topNavigationSecondIconContainer}
              onClick={cancelAppointmentHandler}
            >
              <GetImages name="CancelIcon" width="28" height="24" />
              <Typography component={"p"} className={style.cancelText}>
                <FormattedMessage id="cancel" defaultMessage="Cancel" />
              </Typography>
            </Grid>
          )}
        </Grid>
      </Grid>
      <Overlays />
    </>
  );
};

export default MobileTopNavigation;
